import { defineComponent as _defineComponent } from 'vue'
import { computed, ref } from 'vue';

import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import MotorsportEventCard from '@/features/sports/components/global/MotorsportEventCard.vue';
import GameEventStatus from '@/features/sports/components/ui/GameEventStatus.vue';

import { MultiStepSportEvent, ObjectType, SportEventStatus } from '@/@types/graphql-types';

import { BuyboxParam, trackClickOutSports } from '@/helpers/offer-helper';

import { ClickoutUrl } from '@/helpers/clickout-helper';
import { formatDate as helperFormatDate, formatTime as helperFormatTime } from '@/helpers/time-zone-helper';

import type { SportEventV2, SportOffer } from '@/@types/graphql-types';
import { SnowplowSportContext } from '@/helpers/tracking/providers';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { SponsoredClickoutsHelper } from '@/features/sponsored-recommendations/helpers/sponsored-clickouts-helper';
import { faPlay, faTimes } from '@fortawesome/free-solid-svg-icons';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useAppStore } from '@/stores/pinia/useAppStore';


export default /*#__PURE__*/_defineComponent({
  __name: 'EventCardOld',
  props: {
    event: null,
    bucketIndex: { default: undefined },
    eventIndex: { default: undefined }
  },
  setup(__props: any) {

const props = __props as { event: MultiStepSportEvent, bucketIndex: number, eventIndex: number };



const { locales, webLocale, country, language } = storeToRefs(useLanguageStore());
const { timeZone } = storeToRefs(useUserStore());
const { xServeTrack } = storeToRefs(useAppStore());

const expandOffers = ref(false);

const offers = computed(() => props.event.offers.map(el => ({ ...el, clickoutUrl: '' })) ?? []);
const offerUrls = computed(() => offers.value.map(offer => getOfferClickoutUrl(offer)));

const watchNowOffer = computed<SportOffer | null>(() => props.event.watchNowOffer ?? null);
const watchNowUrl = computed(() => (watchNowOffer.value ? getOfferClickoutUrl(watchNowOffer.value) : null));

const offerCount = computed(() => props.event.offerCount ?? null);

const eventStatus = computed(() => props.event.content?.status);

const isCancelled = computed(() => eventStatus.value === SportEventStatus.Cancelled);

const isNotUpcoming = computed(() =>
	[
		SportEventStatus.Cancelled,
		SportEventStatus.Closed,
		SportEventStatus.Delayed,
		SportEventStatus.Ended,
		SportEventStatus.Live,
		SportEventStatus.Postponed,
		SportEventStatus.Suspended,
	].includes(eventStatus.value)
);

const sportContext = computed(() => {
	if ('parent' in props.event) {
		// MultiStepSportEvent
		return SnowplowSportContext.fromSportEventV2(props.event.sport, ObjectType.SportEvent, props.event);
	}

	// Fallback if event node has no status e.g. due to status living in parent event for formula_racing
	if (!props.event.content.status) (props.event.content as any).status = eventStatus.value;

	return SnowplowSportContext.fromSportEventV2(props.event.sport, ObjectType.SportEvent, props.event);
});

const sport = computed(() => props.event.sport);

function linkRel(offer: SportEventV2['watchNowOffer'] | SportOffer) {
	if (offer == null) return 'noopener noreferrer';

	return SponsoredClickoutsHelper.isSponsoredOfferGraphql(offer.package.packageId, webLocale.value)
		? 'noopener noreferrer sponsored'
		: 'noopener noreferrer';
}

function formatDate(ts: Date) {
	return helperFormatDate(ts, locales.value, webLocale.value, timeZone.value, language.value);
}

function formatTime(ts: Date) {
	return helperFormatTime(ts, locales.value, webLocale.value, timeZone.value, language.value);
}

function getOfferClickoutUrl(offer: SportOffer) {
	return new ClickoutUrl(offer)
		.addContexts(sportContext.value)
		.set('uct_country', country.value)
		.set('uct_buybox', BuyboxParam.Sport)
		.set('uct_sport', sport.value)
		.setCanary(xServeTrack.value);
}

function handleOffersClick() {
	expandOffers.value = !expandOffers.value;

	TrackingHelper.trackEvent(
		'userinteraction',
		{ action: 'more_providers_click', label: props.event.sport, property: 'card' },
		[sportContext.value]
	);
}

function handleClickOut(offer: SportOffer) {
	trackClickOutSports(offer, props.bucketIndex, props.eventIndex, offer.sportObjectType, 'card', [
		sportContext.value,
	]);
}

function handleEventClick() {
	TrackingHelper.trackEvent('userinteraction', { action: 'event_clicked', property: 'card', useLastRoute: true }, [
		sportContext.value,
	]);
}

return { __sfc: true,props, locales, webLocale, country, language, timeZone, xServeTrack, expandOffers, offers, offerUrls, watchNowOffer, watchNowUrl, offerCount, eventStatus, isCancelled, isNotUpcoming, sportContext, sport, linkRel, formatDate, formatTime, getOfferClickoutUrl, handleOffersClick, handleClickOut, handleEventClick, ProviderIcon, MotorsportEventCard, GameEventStatus, faPlay, faTimes }
}

})